/* eslint-disable react/display-name */
/* eslint-disable no-undef */
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { utilities } from '@weatherbug/common';
import { reduxConnect } from '../../common/';
import { 
    getWidgetLocationDetailsFromPostalCodeAction,
    getWidgetLocationDetailsByCityIdAction,
    getDefaultWidgetLocationFromUserIpAction,
    getWidgetCurrentStationObs,
    getWidgetHourlyForecastAction
} from '../../common/actions/weatherWidgetsActions';
import { WeatherWidgetView } from './weatherWidgetView';
import { getQueryParameterByName } from '../../common/utilities/queryParameters';

const forecastDataOffset = 0;
const defaultProviderId = 3;
const locationType = 'latitudelongitude';
const forecastIntervalTime = 60 * 60 * 1000;

const temperatureUnitUtils = utilities.units;
interface WeatherWidgetScreenProps {
    authId: string;
    secret: string;
    obsBaseUrl: string;
    locationBaseUrl: string;
    forecastBaseUrl: string;
    locationLoadError: boolean;
    stdParameters: object;
    widgetLocationName?: string;
    currentTemperature?: number;
    widgetLocationLat?: number;
    widgetLocationLon?: number;
    defaultLocationName?: string;
    defaultLatitude?: number;
    defaultLongitude?: number;
    getWidgetLocationDetailsFromPostalCodeAction(postalCode: string, maxResults: number, locationBaseUrl: string, authId: string, secret: string, verbose: boolean): void;
    getWidgetCurrentStationObs(
        forecastBaseUrl: string, 
        authId: string, 
        secret: string, 
        location: string, 
        locationtype: string, 
        offset: number, 
        units: string ): void;
    getWidgetHourlyForecastAction(forecastBaseUrl: string, authId: string, secret: string, location: string, locationtype: string, offset: number, units: string): void;
    getWidgetLocationDetailsByCityIdAction(cityId: string,  locationBaseUrl: string, authId: string, secret: string): void;
    getDefaultWidgetLocationFromUserIpAction(locationName: string, lat: number, lon: number): void;
}

interface WidgetParams {
    cityCode: string;
    cityId: string;
    postalCode: string;
    countryCode: string;
    widgetSize: string;
    stationId: string;
    providerId: string;
    widgetUnits: string;
}

const mapStoreStateToProps = storeState => {
    const { weatherWidget } = storeState || {};
    const { widgetLocationName, currentTemperature, widgetLocationLat, widgetLocationLon, locationLoadError } = weatherWidget || {};
    const appState = storeState.appState.appState || {};
    const { authId, secret, obsBaseUrl, stdParameters, locationBaseUrl, user, forecastBaseUrl } = appState;
    const { ActiveLocation } = user || {};
    const defaultLocationName = ActiveLocation?.Locality?.DisplayCompositeName;
    const defaultLatitude = ActiveLocation?.Locality?.Latitude;
    const defaultLongitude = ActiveLocation?.Locality?.Longitude;

    return {
        authId,
        secret,
        obsBaseUrl,
        stdParameters,
        locationBaseUrl,
        forecastBaseUrl,
        widgetLocationName,
        currentTemperature,
        locationLoadError,
        widgetLocationLat,
        widgetLocationLon,
        defaultLocationName,
        defaultLatitude,
        defaultLongitude
    };
};

const mapDispatchToProps = {
    getWidgetLocationDetailsFromPostalCodeAction,
    getWidgetCurrentStationObs,
    getWidgetHourlyForecastAction,
    getWidgetLocationDetailsByCityIdAction,
    getDefaultWidgetLocationFromUserIpAction
};

const WeatherWidgetScreen = reduxConnect( mapStoreStateToProps, mapDispatchToProps )(memo((
    { 
        authId,
        secret,
        locationBaseUrl,
        forecastBaseUrl,
        widgetLocationName,
        currentTemperature,
        widgetLocationLat,
        widgetLocationLon,
        defaultLocationName,
        defaultLatitude,
        defaultLongitude,
        locationLoadError,
        getWidgetLocationDetailsFromPostalCodeAction,
        getWidgetCurrentStationObs,
        getWidgetHourlyForecastAction,
        getWidgetLocationDetailsByCityIdAction,
        getDefaultWidgetLocationFromUserIpAction
    } : WeatherWidgetScreenProps
) => {

    const widgetUrlParams = useRef<Partial<WidgetParams> | null>(null);
    const isDataFirstLoad = useRef<boolean>(true);
    const isHourlyForecastLoaded = useRef<boolean>(false);
    const forecastInterval = useRef<ReturnType<typeof setInterval>>();

    useEffect(() => {
        const cityCode = getQueryParameterByName('CityCode');
        const cityId = getQueryParameterByName('CityID');
        const postalCode = getQueryParameterByName('ZipCode');
        const countryCode = getQueryParameterByName('CountryCode');
        const size = getQueryParameterByName('Size');
        const stationId = getQueryParameterByName('StationID');
        const providerId = getQueryParameterByName('ProviderID');
        const units = getQueryParameterByName('units');

        const unitSystems = temperatureUnitUtils?.unitSystems;
        let widgetForecastUnits: string = unitSystems?.english;

        if (units && units === unitSystems?.celsius) {
            widgetForecastUnits = unitSystems.metric;
        }

        widgetUrlParams.current = {
            cityCode,
            cityId,
            postalCode,
            widgetSize: size,
            stationId,
            providerId,
            widgetUnits: widgetForecastUnits
        };
    }, []);

    const fetchWidgetData = useCallback(async() => {
        if (authId && secret && locationBaseUrl && widgetUrlParams.current && isDataFirstLoad.current) {
            const { postalCode, cityId, stationId, providerId, widgetUnits } = widgetUrlParams.current;

            if (postalCode) {
                getWidgetLocationDetailsFromPostalCodeAction(postalCode, 1, locationBaseUrl, authId, secret, true);
            } else if (cityId) {
                getWidgetLocationDetailsByCityIdAction(cityId, locationBaseUrl, authId, secret);
            } else if (stationId) {
                const stationProviderId = providerId ? providerId : defaultProviderId; // defaults to EN providerId
                getWidgetCurrentStationObs(forecastBaseUrl, authId, secret, `${stationProviderId},${stationId}`, 'station', forecastDataOffset, widgetUnits);
            } else if (defaultLatitude && defaultLongitude && defaultLocationName) {
                getDefaultWidgetLocationFromUserIpAction(defaultLocationName, defaultLatitude, defaultLongitude);
            }

            isDataFirstLoad.current = false;
        }
    }, [
        authId, 
        secret, 
        locationBaseUrl,
        defaultLocationName,
        defaultLatitude,
        defaultLongitude, 
        widgetUrlParams?.current?.countryCode, 
        widgetUrlParams?.current?.postalCode, 
        widgetUrlParams?.current?.cityId
    ]);

    useEffect(() => {
        if (isDataFirstLoad.current) {
            fetchWidgetData(); 
        }
    }, [fetchWidgetData]);

    useEffect(() => {
        if (!isHourlyForecastLoaded.current && !isDataFirstLoad.current) {
            if (widgetLocationName && !currentTemperature && widgetLocationLon && widgetLocationLat) {
                getWidgetHourlyForecastAction(
                    forecastBaseUrl, 
                    authId, 
                    secret, 
                    `${widgetLocationLat},${widgetLocationLon}`, 
                    locationType, 
                    forecastDataOffset, 
                    widgetUrlParams.current?.widgetUnits);
                isHourlyForecastLoaded.current = true;
            }
        }
    }, [isDataFirstLoad.current, widgetLocationName, currentTemperature, widgetLocationLat, widgetLocationLon]);

    useEffect(() => {
        if (!isDataFirstLoad.current && currentTemperature && widgetLocationLon && widgetLocationLat && authId && secret && !forecastInterval.current) {
            forecastInterval.current = setInterval(
                () => {
                    getWidgetHourlyForecastAction(
                        forecastBaseUrl, 
                        authId, 
                        secret, 
                        `${widgetLocationLat},${widgetLocationLon}`, 
                        locationType, 
                        forecastDataOffset, 
                        widgetUrlParams.current?.widgetUnits);
                }, forecastIntervalTime);
        }

        return () => {
            if (forecastInterval.current) {
                clearInterval(forecastInterval.current);
            }
        };
    }, [currentTemperature, widgetLocationLat, widgetLocationLon, isDataFirstLoad.current]);

    useEffect(() => {
        // fall back if there was a PostalCode or CityId but data load failed
        if (!isDataFirstLoad.current && locationLoadError && defaultLatitude && defaultLongitude && defaultLocationName) {
            getDefaultWidgetLocationFromUserIpAction(defaultLocationName, defaultLatitude, defaultLongitude);
        }
    }, [isDataFirstLoad.current, locationLoadError, defaultLocationName, defaultLatitude, defaultLongitude]);

    return (
        <WeatherWidgetView />
    );
}));

WeatherWidgetScreen.displayName = 'WeatherWidgetScreen';

export { WeatherWidgetScreen as default };
